import { apply_markup_to_number } from '@liveflight/utilities/flight-offer';
import {
  displayedAvailability,
  isBlackListedByCarrier,
  notActuallySeats,
  SEAT_SUITABLE_FOR_INFANT,
} from '@liveflight/resources/seat-map';
import { is_child, is_infant } from '@liveflight/utilities/mixins';
import { log } from '@/utilities/commons';

const notActuallySeatsCodes = new Set(Object.keys(notActuallySeats));

const isActualSeat = (seat) => {
  if (!seat) return false;

  const { characteristicsCodes = [] } = seat;

  if (!characteristicsCodes.length) return true;

  return !characteristicsCodes.some((code) => notActuallySeatsCodes.has(code));
};

export const filterHeldPassengers = (passengers) =>
  passengers.filter((p) => {
    if (p.travelerType) return p.travelerType !== 'HELD_INFANT';
    return p.traveler_type !== 'HELD_INFANT';
  });

const travelerTypePriority = (passenger) => {
  if (is_infant(passenger) || passenger.holding) return 2;
  if (is_child(passenger)) return 1;
  return 0;
};

export const sortPassengers = (passengers) =>
  [...passengers].sort((p1, p2) => travelerTypePriority(p2) - travelerTypePriority(p1));

const parseSeatNumber = (seatNumber) => ({
  row: seatNumber.substring(0, seatNumber.length - 1),
  column: seatNumber[seatNumber.length - 1],
});

export const isSeatAvailableForAnyPassenger = (seat) => {
  const { travelerPricing = [] } = seat || {};
  if (!travelerPricing.length) {
    return false
  }
  return travelerPricing.some(
    (pricing) => pricing.seatAvailabilityStatus.toUpperCase() === 'AVAILABLE'
  );
};

export const isSeatAvailableForAllPassengers = (seat) => {
  const { travelerPricing = [] } = seat || {};
  if (!travelerPricing.length) {
    return false
  }
  return travelerPricing.every(
    (pricing) => pricing.seatAvailabilityStatus.toUpperCase() === 'AVAILABLE'
  );
};

export const isSeatAvailableForPassenger = (travelerId, seat) => {
  if (!seat) {
    return false;
  }
  const travelerPricing = (seat.travelerPricing || []).find(
    (pricing) => pricing.travelerId === travelerId
  );
  return (
    !!travelerPricing &&
    String(travelerPricing.seatAvailabilityStatus).toUpperCase() === 'AVAILABLE'
  );
};

const isSeatBlackListedByCarrier = (seat, carrier) => {
  const checker = isBlackListedByCarrier[carrier];
  return checker && checker(seat.characteristicsCodes);
};

export const mapSeatObjectsToGrid = (flightOffer, data, dictionary = {}) => {
  const aircraftMap = {};
  const columns = {};
  const rows = {};
  const priceMap = {};
  const availableSeatsRemoved = {};
  const lastSeatPerRow = {};

  const { deckConfiguration, seats = [], facilities = [], carrierCode } = data;
  const { width, exitRowsX: exitRows = [] } = deckConfiguration;
  let hasSeatsSuitableForInfant = false;

  for (let col = 0; col < width; col += 1) {
    columns[col] = { name: '', isHallway: true };
  }

  seats.forEach((seat) => {
    const { coordinates } = seat;
    const { x, y } = coordinates;
    if (!aircraftMap[x]) {
      aircraftMap[x] = { blocks: {} };
    }
    if (Number.isNaN(y) || Number.isNaN(x)) {
      return;
    }

    const { row: seatRow, column: seatColumn } = parseSeatNumber(seat.number);
    const actualSeat = isActualSeat(seat);
    const carrierBlackListed = isSeatBlackListedByCarrier(seat, carrierCode);

    // Single-pass: availability tracking + status normalization + pricing build
    let rawAnyAvailable = false;
    let rawAllAvailable = true;
    let postBlockingAnyAvailable = false;

    const processedPricing = seat.travelerPricing.map((pricing) => {
      const hasPrice = !!pricing.price;
      let seatAvailabilityStatus = (pricing.seatAvailabilityStatus || 'BLOCKED').toUpperCase();
      let blackListed = false;

      // Track raw availability before blocking
      if (seatAvailabilityStatus === 'AVAILABLE') {
        rawAnyAvailable = true;
      } else {
        rawAllAvailable = false;
      }

      if (seatAvailabilityStatus === 'AVAILABLE') {
        if (!actualSeat || !hasPrice || carrierBlackListed) {
          seatAvailabilityStatus = 'BLOCKED';
          blackListed = true;
          availableSeatsRemoved[pricing.travelerId] =
            (availableSeatsRemoved[pricing.travelerId] || 0) + 1;
        }
      }

      // Track post-blocking availability
      if (seatAvailabilityStatus === 'AVAILABLE') {
        postBlockingAnyAvailable = true;
      }

      if (!hasPrice || !pricing.price.total || !flightOffer || !flightOffer.travelerPricings) {
        return {
          ...pricing,
          seatAvailabilityStatus,
          blackListed,
        };
      }

      const total = pricing.price.total;
      let grandTotal = priceMap[total];
      if (!grandTotal) {
        grandTotal = apply_markup_to_number(flightOffer, Number(total));
        priceMap[total] = grandTotal;
      }

      return {
        ...pricing,
        seatAvailabilityStatus,
        blackListed,
        price: { ...pricing.price, grandTotal },
      };
    });

    // If no pricing had AVAILABLE, rawAllAvailable stays true but rawAnyAvailable is false
    if (!rawAnyAvailable) {
      rawAllAvailable = false;
    }

    const block = {
      ...seat,
      isPartiallyAvailable: rawAnyAvailable && !rawAllAvailable,
      travelerPricing: processedPricing,
      row: seatRow,
      column: seatColumn,
      type: 'seat',
    };
    aircraftMap[x].blocks[y] = block;

    // O(1) neighbor linking via lastSeatPerRow
    const prevY = lastSeatPerRow[x];
    if (prevY !== undefined) {
      const seatToTheLeft = aircraftMap[x].blocks[prevY];
      block.left = {
        number: seatToTheLeft.number,
        isBlocked: !seatToTheLeft._postBlockingAvailable,
      };
      seatToTheLeft.right = {
        number: seat.number,
        isBlocked: !rawAnyAvailable,
      };
    }
    lastSeatPerRow[x] = y;

    // Store on block for neighbor linking of future seats to the right
    block._postBlockingAvailable = postBlockingAnyAvailable;

    const characteristicsCodes = seat.characteristicsCodes || [];
    if (characteristicsCodes.includes(SEAT_SUITABLE_FOR_INFANT) && rawAnyAvailable) {
      hasSeatsSuitableForInfant = true;
    }

    columns[y] = { name: seatColumn, isHallway: false };
    rows[x] = Number(seatRow);
  });

  // Clean up temporary neighbor-linking flag
  Object.keys(aircraftMap).forEach((row) => {
    const blocks = aircraftMap[row].blocks || {};
    Object.keys(blocks).forEach((col) => {
      delete blocks[col]._postBlockingAvailable;
    });
  });

  const facilitiesDictionary = dictionary.facilities || {};
  facilities.forEach((facility, i) => {
    const { coordinates } = facility;
    const { x, y } = coordinates;
    if (!aircraftMap[x]) {
      aircraftMap[x] = { blocks: {} };
    }
    if (Number.isNaN(y) || Number.isNaN(x)) {
      return;
    }
    aircraftMap[x].blocks[y] = {
      ...facility,
      facilityId: [facility.position, facility.column, facility.row, facility.code, i]
        .filter((f) => f !== undefined && f !== null && f !== '')
        .join('-'),
      description: facilitiesDictionary[facility.code],
      type: 'facility',
    };
  });

  exitRows.forEach((exit) => {
    if (!aircraftMap[exit]) {
      aircraftMap[exit] = { exits: {} };
    }
    // Mutate directly instead of spreading
    aircraftMap[exit].exits = {
      0: { type: 'exit', direction: 'left' },
      [width - 1]: { type: 'exit', direction: 'right' },
    };
    for (let col = 1; col < width - 1; col += 1) {
      aircraftMap[exit].exits[col] = { type: 'empty' };
    }
  });

  // Fill empty spaces — unconditionally fill, fallback handles existing blocks
  try {
    Object.keys(aircraftMap).forEach((row) => {
      const blocks = (aircraftMap[row] || {}).blocks || {};
      for (let col = 0; col < width; col += 1) {
        if (!blocks[col]) {
          blocks[col] = { type: 'empty' };
        }
      }
      aircraftMap[row].blocks = blocks;
    });
  } catch (e) {
    log('mapSeatObjectsToGrid error');
    log(e);
  }

  // Adjust hallways
  const columnKeys = Object.keys(columns);
  const adjustedColumns = { ...columns };
  if (columnKeys.length > 2) {
    for (let col = 1; col < columnKeys.length - 1; col += 1) {
      const column = columns[col];
      if (column.isHallway) {
        const leftIsHallway = columns[col - 1] && columns[col - 1].isHallway;
        const rightIsHallway = columns[col + 1] && columns[col + 1].isHallway;
        adjustedColumns[col] = {
          name: column.name,
          isHallway: (leftIsHallway && rightIsHallway) || (!leftIsHallway && !rightIsHallway),
        };
      }
    }
  }

  return {
    hasSeatsSuitableForInfant,
    aircraftMap,
    availableSeatsRemoved,
    columns: adjustedColumns,
    rows,
  };
};

export const getTravelerPricing = (seat, travelerId, fallbackToDefault = true) => {
  if (!seat) {
    return {};
  }
  const { travelerPricing = [] } = seat;
  const pricingIndex = travelerPricing.findIndex((pricing) => pricing.travelerId === travelerId);
  if (!fallbackToDefault) {
    return pricingIndex >= 0 ? travelerPricing[pricingIndex] : {};
  }
  if (travelerPricing && travelerPricing.length) {
    return travelerPricing[Math.max(0, pricingIndex)];
  }
  return {};
};

export const getSeatPrice = (seat, travelerId, markedUp = true) => {
  const { price: seatPrice } = getTravelerPricing(seat, travelerId, false);
  if (!seatPrice) {
    return 0;
  }

  let grandTotal = seatPrice.total || 0;
  if (markedUp) {
    grandTotal = seatPrice.grandTotal || seatPrice.total;
  }
  // Keep it to cents decimal
  return Math.ceil(Number(grandTotal) * 100) / 100;
};

export const getSeatsForTraveler = (flightOffer, travelerId) => {
  const seats = [];
  try {
    const pricings = flightOffer.travelerPricings.filter((t) => t.travelerId === travelerId);
    for (let i = 0; i < pricings.length; i += 1) {
      const fareDetailsBySegment = pricings[i].fareDetailsBySegment;
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          const fare = fareDetailsBySegment[j];
          if (fare.additionalServices && fare.additionalServices.chargeableSeatNumber) {
            seats.push({
              segmentId: fare.segmentId,
              number: fare.additionalServices.chargeableSeatNumber,
            });
          }
        }
      }
    }
  } catch (e) {
    log('getSeatsForTraveler error');
    log(e);
  }
  return seats;
};

export const getSeatsForSegment = (flightOffer, segmentId) => {
  const seats = [];
  try {
    const pricings = flightOffer.travelerPricings;
    for (let i = 0; i < pricings.length; i += 1) {
      const travelerId = pricings[i].travelerId;
      const fares = pricings[i].fareDetailsBySegment;
      for (let j = 0; j < fares.length; j += 1) {
        const fare = fares[j];
        if (fare.segmentId === segmentId && fare.additionalServices && fare.additionalServices.chargeableSeatNumber) {
          seats.push({
            travelerId,
            number: fare.additionalServices.chargeableSeatNumber,
          });
        }
      }
    }
  } catch (e) {
    log('getSeatsForSegment error');
    log(e);
  }
  return seats;
};

export const getSegmentsFromFlightOffers = (flightOffers) => {
  const segmentsMap = {};
  const allSegments = flightOffers.flatMap((offer) =>
    offer.itineraries.flatMap((itinerary) => itinerary.segments)
  );
  for (let index = 0; index < allSegments.length; index++) {
    const segment = allSegments[index];
    segmentsMap[segment.id] = segment;
  }
  return segmentsMap;
};

export const getDisplayAvailability = (availability) => {
  const key = (availability || '').toLowerCase();
  return displayedAvailability[key] || availability;
};

export const calculate_total_seats_price = (selections) =>
  Object.values(selections).reduce(
    (total, segment_seats) =>
      total +
      Object.entries(segment_seats).reduce(
        (segment_total, [traveler_id, seat]) =>
          segment_total + getSeatPrice(seat, traveler_id),
        0
      ),
    0
  );

export const are_passengers_seated_on_segment = (selections, segment_id) =>
  Object.keys(selections[segment_id] || {}).length > 0;

export const is_everyone_seated = (selections, segment_id, passenger_count) =>
  Object.keys(selections[segment_id] || {}).length === passenger_count;

/**
 * ITSM-513: Validates that every non-HELD_INFANT traveler has pricing data
 * in at least one seat across the entire seatmap response.
 */
export const validate_traveler_pricing = (flight_offer, seat_map_data) => {
  const { travelerPricings = [] } = flight_offer || {};
  const traveler_ids = travelerPricings
    .filter((t) => t.travelerType !== 'HELD_INFANT')
    .map((t) => t.travelerId);

  const all_seats = (seat_map_data || []).flatMap((segment) =>
    (segment.decks || []).flatMap((deck) => deck.seats || [])
  );

  if (!traveler_ids.length || !all_seats.length) return false;

  return traveler_ids.every((id) =>
    all_seats.some((seat) =>
      (seat.travelerPricing || []).some((p) => p.travelerId === id)
    )
  );
};

/**
 * ITSM-513: Validates that deckConfiguration.width is consistent with
 * the actual Y-coordinates of the returned seats.
 */
export const validate_deck_configuration = (deck_configuration, seats) => {
  const { width } = deck_configuration || {};
  if (!width || width <= 0) return false;
  if (!seats || !seats.length) return true;

  const max_y = seats.reduce((max, seat) => {
    const y = seat && seat.coordinates && seat.coordinates.y;
    return (y !== undefined && !Number.isNaN(y) && y > max) ? y : max;
  }, -1);

  if (max_y < 0) return false;

  // Reject overflow (seats beyond grid), allow underflow (grid wider than seats e.g. narrow tail)
  return max_y <= (width - 1);
};
